import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

interface PageState {
  rootId?: string;
  isAuthoring: boolean;
}

export const key = new PluginKey<PageState>("pageState");

/** Creates a plugin that manages the page authoring state. */
export function pageStatePlugin(initialState: PageState): Plugin<PageState> {
  return new Plugin<PageState>({
    key,

    state: {
      init() {
        return initialState;
      },

      apply(tr, value) {
        const update = tr.getMeta(key) as Partial<PageState> | undefined;
        if (update !== undefined) {
          return { ...value, ...update };
        }
        return value;
      },
    },
    props: {
      // eslint-disable-next-line @typescript-eslint/no-empty-object-type
      attributes: (state): { "data-authoring-page": "true" } | {} => {
        const { isAuthoring } = key.getState(state) || { isAuthoring: false };
        if (isAuthoring) {
          return { "data-authoring-page": "true" };
        }
        return {};
      },
      // When `isAuthoring` changes, we need to send a message to the image nodes
      // so they can redraw themselves. We can use decorations to do this.
      decorations(state) {
        const { isAuthoring } = key.getState(state) || { isAuthoring: false };

        if (!isAuthoring) {
          return DecorationSet.empty;
        }

        const decorations: Decoration[] = [];

        state.doc.nodesBetween(0, state.doc.content.size, (node, pos) => {
          if (node.type.name === "image") {
            decorations.push(Decoration.node(pos, pos + 1, { "data-authoring-image": "true" }));
          }
        });

        return DecorationSet.create(state.doc, decorations);
      },
    },
  });
}
